import { useCallback, useRef, } from 'react';
const DRAG_TOLERANCE = 10;
function determineDragIntention(touch, moveStart) {
    const { clientX, clientY } = touch;
    const deltaX = Math.abs(moveStart.x - clientX);
    const deltaY = Math.abs(moveStart.y - clientY);
    if (deltaX > deltaY) {
        return 'horizontal';
    }
    if (deltaY > deltaX) {
        return 'vertical';
    }
    return null;
}
function isDragEvent(clientX, currentX) {
    return Boolean(currentX && Math.abs(currentX - clientX) > DRAG_TOLERANCE);
}
function isTouchEvent(event) {
    return (Boolean(window.TouchEvent && event instanceof TouchEvent) ||
        'touches' in event);
}
export function useDragHandlers(updateFunction, dragStartCallback, dragEndCallback, forceDisableMove) {
    const dragStart = useRef(null);
    const moveIntention = useRef(null);
    const handleDragMove = useCallback((e) => {
        const event = isTouchEvent(e) ? e.changedTouches[0] : e;
        if (!dragStart.current || forceDisableMove) {
            return;
        }
        // determine direction of move intention, this is required to
        if (!moveIntention.current) {
            moveIntention.current = determineDragIntention(event, dragStart.current);
        }
        // skip when scrolling
        if (moveIntention.current !== 'horizontal') {
            return;
        }
        const { clientX } = event;
        if (!isDragEvent(clientX, dragStart.current.x)) {
            return;
        }
        if (e.cancelable) {
            e.preventDefault();
        }
        updateFunction(clientX, dragStart.current);
    }, [forceDisableMove, updateFunction]);
    const handleDragEnd = useCallback(() => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove, false);
        window.removeEventListener('touchend', handleDragEnd, false);
        document.body.style.cursor = 'auto';
        if (!dragStart.current) {
            return;
        }
        dragStart.current = null;
        moveIntention.current = null;
        dragEndCallback === null || dragEndCallback === void 0 ? void 0 : dragEndCallback();
    }, [handleDragMove, dragEndCallback]);
    return useCallback((e) => {
        const event = isTouchEvent(e) ? e.changedTouches[0] : e;
        if (event) {
            window.addEventListener('mousemove', handleDragMove);
            window.addEventListener('mouseup', handleDragEnd);
            window.addEventListener('touchmove', handleDragMove, {
                passive: false,
            });
            window.addEventListener('touchend', handleDragEnd, {
                passive: false,
            });
            document.body.style.cursor = 'ew-resize';
            dragStart.current = {
                x: event.clientX,
                y: event.clientY,
            };
            dragStartCallback === null || dragStartCallback === void 0 ? void 0 : dragStartCallback();
        }
    }, [dragStartCallback, handleDragEnd, handleDragMove]);
}
